import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import MangaCard from '@/pages/home/components/MangaCard';
import { getMangaByCategory, categoryMap } from '@/mocks/manga';
import type { MangaItem } from '@/mocks/manga';

const tabSlugToCategory: Record<string, string> = {
  all: 'all',
  korean: '韩漫',
  japanese: '日漫',
  chinese: '国漫',
};

const tabs = [
  { label: '全部', slug: 'all', icon: 'ri-global-line' },
  { label: '韩漫', slug: 'korean', icon: 'ri-heart-line' },
  { label: '日漫', slug: 'japanese', icon: 'ri-star-line' },
  { label: '国漫', slug: 'chinese', icon: 'ri-flag-line' },
];

export default function CategoryPage() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const [mangaList, setMangaList] = useState<MangaItem[]>([]);
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  const category = tabSlugToCategory[slug] || 'all';
  const categoryName = category === 'all' ? '全部漫画' : category;

  useEffect(() => {
    let list = getMangaByCategory(category);
    if (sortBy === 'popular') {
      list = [...list].sort((a, b) => b.views - a.views);
    } else if (sortBy === 'rating') {
      list = [...list].sort((a, b) => b.rating - a.rating);
    } else {
      list = [...list].sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
    }
    setMangaList(list);
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [category, sortBy]);

  useEffect(() => {
    setSortBy('latest');
  }, [slug]);

  const sortLabels: Record<string, string> = {
    latest: '最新更新',
    popular: '最多阅读',
    rating: '最高评分',
  };

  const breadcrumbs = [
    { label: '首页', path: '/' },
    { label: categoryName, path: `/category/${slug}` },
  ];

  return (
    <>
      <Navbar />
      <ScrollToTop />

      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-3">
            <nav aria-label="面包屑导航" className="flex items-center gap-2 text-xs text-foreground-500">
              {breadcrumbs.map((crumb, idx) => (
                <span key={crumb.path} className="flex items-center gap-2">
                  {idx > 0 && (
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-arrow-right-s-line"></i>
                    </div>
                  )}
                  {idx === breadcrumbs.length - 1 ? (
                    <span className="text-foreground-700 font-medium">{crumb.label}</span>
                  ) : (
                    <Link to={crumb.path} className="hover:text-primary-500 transition-colors cursor-pointer">
                      {crumb.label}
                    </Link>
                  )}
                </span>
              ))}
            </nav>
          </div>
        </div>

        {/* Category Header */}
        <section className="py-8 md:py-10 px-4 md:px-6 bg-background-50">
          <div className="max-w-[1280px] mx-auto">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
              {categoryName}
            </h1>
            <p className="text-sm text-foreground-500">
              {category === 'all'
                ? '浏览全部韩漫、日漫、国漫，发现你喜爱的漫画'
                : `精选${category}漫画，免费全集下拉式阅读`}
            </p>
          </div>
        </section>

        {/* Tabs */}
        <div className="sticky top-16 z-30 bg-background-50 border-b border-background-200/70">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1 py-3">
                {tabs.map((tab) => (
                  <Link
                    key={tab.slug}
                    to={`/category/${tab.slug}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-1.5 cursor-pointer whitespace-nowrap ${
                      slug === tab.slug
                        ? 'bg-primary-500 text-white'
                        : 'text-foreground-600 hover:text-primary-500 hover:bg-primary-50'
                    }`}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={tab.icon}></i>
                    </div>
                    {tab.label}
                  </Link>
                ))}
              </div>

              <div className="relative">
                <button
                  onClick={() => setShowSortDropdown(!showSortDropdown)}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm text-foreground-600 hover:text-primary-500 transition-colors rounded-md cursor-pointer whitespace-nowrap"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-sort-desc"></i>
                  </div>
                  {sortLabels[sortBy]}
                </button>

                {showSortDropdown && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowSortDropdown(false)}></div>
                    <div className="absolute right-0 top-full mt-1 z-50 bg-background-50 rounded-lg border border-background-200/70 py-1 w-36">
                      {Object.entries(sortLabels).map(([key, label]) => (
                        <button
                          key={key}
                          onClick={() => { setSortBy(key as typeof sortBy); setShowSortDropdown(false); }}
                          className={`w-full text-left px-4 py-2 text-sm transition-colors cursor-pointer whitespace-nowrap ${
                            sortBy === key
                              ? 'text-primary-500 bg-primary-50 font-medium'
                              : 'text-foreground-600 hover:bg-background-100'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Manga Grid */}
        <section className="py-8 md:py-10 px-4 md:px-6 bg-background-50">
          <div className="max-w-[1280px] mx-auto">
            {mangaList.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5 md:gap-6">
                {mangaList.map((manga) => (
                  <MangaCard key={manga.id} manga={manga} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-200">
                  <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">暂无相关漫画</p>
              </div>
            )}

            <div className="text-center mt-8 text-sm text-foreground-500">
              共 {mangaList.length} 部漫画
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${categoryName} - 樱花漫画`,
            description: `樱花漫画${categoryName}专区，提供免费全集下拉式阅读`,
            url: `https://www.yinghuamanhua.com/category/${slug}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '樱花漫画',
              url: 'https://www.yinghuamanhua.com',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.yinghuamanhua.com' },
                { '@type': 'ListItem', position: 2, name: categoryName, item: `https://www.yinghuamanhua.com/category/${slug}` },
              ],
            },
          }),
        }}
      />
    </>
  );
}